import { FlatButton, HoverBorderButton, StyledButton } from '@elements/Button';
import {
  FlexRow,
  BoldText,
  MuteText,
  SecondaryHeaderContainer,
} from '@elements/Common';
import { ControlledTextFormField } from '@elements/FormField';
import { useState } from 'react';
import {
  POStatus,
  POStatusImage,
  POStatusGreyImage,
} from '@utility/constants/common';
import { CommonUtility } from '@utility/common';
import PurchaseOrdersService from '@utility/services/purchase-order';
import {
  ArrowRight,
  Calendar,
  Checks,
  Clock,
  Copy,
  CurrencyDollar,
  Package,
  Plus,
  Scroll,
  X,
} from '@phosphor-icons/react';
import { ActionDropdown, ActionDropdownMenu } from '@elements/Dropdown';
import { DateFormat, DateUtility } from '@utility/date';
import useUrlPermission from '@hooks/url-permission';
import PermissionTooltip from '@components/PermissionTooltip';
import { cn } from '@skupreme/shared/ui';

const POStatusIndicator = {
  [POStatus.open]: 1,
  [POStatus.adminConfirmation]: 1,
  [POStatus.pendingPayment]: 2,
  [POStatus.inProduction]: 3,
  [POStatus.pendingFinalPayment]: 4,
  [POStatus.readyForShipment]: 5,
  [POStatus.completed]: 5,
};

export const POStatusText = {
  [POStatus.pendingPayment]: 'Initial Payment',
  [POStatus.open]: 'Open',
  [POStatus.adminConfirmation]: 'Open',
  [POStatus.pendingFinalPayment]: 'Final Payment',
  [POStatus.inProduction]: 'In Production',
  [POStatus.readyForShipment]: 'Ready For Shipment',
  [POStatus.completed]: 'Completed',
  [POStatus.archived]: 'Archived',
};

/* A header fact: muted key, then its value. */
export function POCreateHeader({
  cbm,
  totalWeight,
  control,
  backClick,
  errors,
  imperial,
  units,
  cbf,
  markRetroActive,
  isRetroActive,
}) {
  return (
    <>
      <SecondaryHeaderContainer className="dark-bg sk:justify-between!">
        <FlexRow>
          <MuteText className="sk:mr-2!">ID</MuteText>
          <ControlledTextFormField
            error={errors.display_id}
            placeholder="Id"
            name="display_id"
            control={control}
            fieldClass="sk:mb-0!"
          />
        </FlexRow>
        <FlexRow>
          <RetroactiveButton
            markRetroActive={markRetroActive}
            isRetroActive={isRetroActive}
          />
          <HoverBorderButton type="button" onClick={backClick}>
            Cancel
          </HoverBorderButton>
        </FlexRow>
      </SecondaryHeaderContainer>
    </>
  );
}

function RetroactiveButton({ markRetroActive, isRetroActive }) {
  const toggleRetroAcitve = () => {
    markRetroActive(!isRetroActive);
  };

  function ButtonRenderer() {
    if (isRetroActive) {
      return (
        <>
          <Scroll size={32} />
          Mark as Normal Purchase Order
        </>
      );
    }

    return (
      <>
        <Clock size={32} />
        Mark as Retroactive Purchase Order
      </>
    );
  }

  return (
    <ActionDropdown
      button
      className="icon sk:mr-2!"
      direction="left"
      icon="ellipsis horizontal"
      open={false}
      simple
    >
      <ActionDropdownMenu>
        <FlatButton
          className="dropdown-action dropdown-default-action"
          type="button"
          onClick={toggleRetroAcitve}
        >
          <ButtonRenderer />
        </FlatButton>
      </ActionDropdownMenu>
    </ActionDropdown>
  );
}

export function POStatusBox({
  status,
  isPublic,
  title,
  className = 'sk:mr-3.5!',
}) {
  const statusText = title
    ? POStatusIndicator[status] && (
        <MuteText className="sk:shrink-0">
          {POStatusIndicator[status]}/5
        </MuteText>
      )
    : POStatusIndicator[status] && (
        <MuteText>{POStatusIndicator[status]}/5</MuteText>
      );
  return (
    <div
      title={title}
      className={cn(
        `${className} sk:flex! sk:items-center! status-box${
          title ? '' : 'sk:justify-between!'
        }`,
        title && 'sk:w-[200px]',
      )}
    >
      {POStatusGreyImage[status] && (
        <img
          alt={status}
          src={isPublic ? POStatusGreyImage[status] : POStatusImage[status]}
        />
      )}
      <BoldText className={cn('sk:mx-2!', title && 'sk:text-xs')} title={title}>
        {title ?? POStatusText[status]}
      </BoldText>
      {statusText}
    </div>
  );
}

export function DetailsHeader({
  cbm,
  changeStatusClick,
  changeETAClick,
  summary,
  adjustQuantityClick,
  imperial,
  isAdmin,
  canCancel,
  deletePO,
  data,
  adjustPriceClick,
  markAsCompleted,
  completed,
  isPublic,
  cbf,
  addNewItems,
  exportPo,
}) {
  const { disabled, hidden } = useUrlPermission();
  const [privateLink, setPrivateLink] = useState({
    label: 'Copy Link',
    loading: false,
  });

  const copyLinkToClipboard = async e => {
    e.preventDefault();
    try {
      setPrivateLink({
        ...privateLink,
        loading: true,
      });

      const result = await PurchaseOrdersService.publicLink(data?._id);
      CommonUtility.copyToClipboard(result.data.link);

      setPrivateLink({
        label: 'Link Copied!',
        loading: false,
      });
    } catch (error) {
      setPrivateLink({ loading: false, label: 'Error while copying the link' });
    }

    setTimeout(() => {
      setPrivateLink({ loading: false, label: 'Copy Link' });
    }, 3000);
  };

  const permissionProps = {
    disabled,
    hidden,
    wrapperClassName: 'sk:w-full',
    position: 'left center',
  };

  return (
    <>
      <SecondaryHeaderContainer
        className={`dark-bg ${isPublic ? 'full-width' : ''}`}
      >
        {isPublic && (
          <StyledButton type="button" className="sk:mx-1!" onClick={exportPo}>
            Export
          </StyledButton>
        )}
        {/* @ts-expect-error - TS2739: Type '{ status: any; }' is missing the following properties ... */}
        <POStatusBox status={data?.status} />
        <div className="sk:mx-3.5! sk:text-center sk:flex! sk:flex-col!">
          <MuteText>Est. Completion</MuteText>
          <BoldText className="sk:-mt-[3px]">
            {DateUtility.formatDate(
              data?.estimated_completion,
              DateFormat.date,
            )}
          </BoldText>
        </div>
        {(!completed || canCancel) && data?._id && (
          <div className="sk:text-right">
            <ActionDropdown
              button
              className="icon sk:mr-0!"
              direction="left"
              icon="ellipsis horizontal"
              open={false}
              simple
            >
              <ActionDropdownMenu>
                {(!completed || canCancel) && (
                  <FlatButton
                    className="dropdown-action dropdown-default-action"
                    loading={privateLink.loading}
                    onClick={copyLinkToClipboard}
                    type="button"
                  >
                    <Copy size={32} />
                    {privateLink.label}
                  </FlatButton>
                )}
                {!completed && (
                  <>
                    <PermissionTooltip {...permissionProps}>
                      <FlatButton
                        className="dropdown-action dropdown-default-action"
                        onClick={adjustPriceClick}
                        type="button"
                        disabled={disabled}
                      >
                        <CurrencyDollar size={18} />
                        Adjust Cost
                      </FlatButton>
                    </PermissionTooltip>
                    <PermissionTooltip {...permissionProps}>
                      <FlatButton
                        className="dropdown-action dropdown-default-action"
                        onClick={adjustQuantityClick}
                        type="button"
                        disabled={disabled}
                      >
                        <Package size={18} />
                        Adjust Content Quantity
                      </FlatButton>
                    </PermissionTooltip>

                    <PermissionTooltip {...permissionProps}>
                      <FlatButton
                        className="dropdown-action dropdown-default-action"
                        onClick={() => isAdmin && changeStatusClick()}
                        type="button"
                        disabled={disabled}
                      >
                        <ArrowRight size={18} />
                        Change Status
                      </FlatButton>
                    </PermissionTooltip>
                    {addNewItems && (
                      <PermissionTooltip {...permissionProps}>
                        <FlatButton
                          className="dropdown-action dropdown-default-action"
                          disabled={disabled}
                          onClick={() => addNewItems()}
                          type="button"
                        >
                          <Plus size={18} />
                          Add Items
                        </FlatButton>
                      </PermissionTooltip>
                    )}
                  </>
                )}
                {![POStatus.completed, POStatus.archived].includes(
                  data?.status,
                ) && (
                  <PermissionTooltip {...permissionProps}>
                    <FlatButton
                      className="dropdown-action dropdown-default-action"
                      onClick={() => changeETAClick()}
                      type="button"
                      disabled={disabled}
                    >
                      <Calendar size={18} />
                      Change Est. Completion
                    </FlatButton>
                  </PermissionTooltip>
                )}
                {data?.status === POStatus.readyForShipment &&
                  (data?.allocation_percentage || 0) > 0 &&
                  (data?.allocation_percentage || 0) < 100 && (
                    <PermissionTooltip {...permissionProps}>
                      <FlatButton
                        className="dropdown-action dropdown-default-action"
                        onClick={() => markAsCompleted()}
                        type="button"
                        disabled={disabled}
                      >
                        <Checks size={18} />
                        Mark As Completed
                      </FlatButton>
                    </PermissionTooltip>
                  )}
                {canCancel && (
                  <PermissionTooltip {...permissionProps}>
                    <FlatButton
                      className="dropdown-action dropdown-danger-action"
                      disabled={disabled}
                      onClick={deletePO}
                      type="button"
                    >
                      <X className="sk:cursor-pointer" size={18} />
                      Cancel Purchase Order
                    </FlatButton>
                  </PermissionTooltip>
                )}
              </ActionDropdownMenu>
            </ActionDropdown>
          </div>
        )}
      </SecondaryHeaderContainer>
    </>
  );
}

export function POBulkHeader({ onSave, backClick, canCreate }) {
  return (
    <>
      <SecondaryHeaderContainer className="dark-bg">
        <HoverBorderButton type="button" onClick={backClick}>
          Cancel
        </HoverBorderButton>
        {canCreate && (
          <StyledButton type="button" onClick={onSave}>
            Submit
          </StyledButton>
        )}
      </SecondaryHeaderContainer>
    </>
  );
}
